import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import TurnDetail from "../../components/TurnDetail/TurnDetail";
import { Button, message } from "antd";
import {
  DownloadOutlined,
  HomeOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import { canUseWebShare } from "../../utils/device";
import { normalizeConfirmHour } from "../../utils/confirmTurnHour";
import "./ConfirmTurn.scss";

const STORAGE_KEY = "confirmTurnData";

export default function ConfirmTurn() {
  const history = useHistory();
  const location = useLocation();

  const [secondsLeft, setSecondsLeft] = useState(60); // Contador inicial en 60 segundos
  const [turnDetailRef, setTurnDetailRef] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  // Redirige si no hay datos en location.state
  useEffect(() => {
    let data = null;

    if (location.state) {
      data = location.state;
    } else {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          data = JSON.parse(stored);
        } catch (error) {
          history.replace("/");
          return;
        }
      } else {
        history.replace("/");
        return;
      }
    }

    // Siempre normalizar el hour a string ISO antes de usar/guardar
    const normalized = {
      ...data,
      turnDetail: {
        ...data.turnDetail,
        hour: normalizeConfirmHour(data.turnDetail?.hour),
      },
    };

    setConfirmData(normalized);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }, [location.state, history]);

  useEffect(() => {
    if (!confirmData) return;

    // Configura el contador regresivo y redirección
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    const redirectTimer = setTimeout(() => {
      sessionStorage.removeItem(STORAGE_KEY);
      history.replace(`/`);
    }, 60000);

    return () => {
      clearInterval(timer); // Limpia el contador
      clearTimeout(redirectTimer); // Limpia la redirección
    };
  }, [confirmData, history]);

  // Limpiar sessionStorage cuando el usuario navega manualmente al inicio
  const goHome = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    history.push("/");
  };

  // Extraer datos de confirmData
  const turnDetail = confirmData?.turnDetail;
  const turnType = confirmData?.turnType || "";
  const doctor = confirmData?.doctor || "";
  const description = confirmData?.description || null;
  const cancelLink = confirmData?.cancelLink || null;
  const direction = confirmData?.direction || null;
  const phone = confirmData?.phone || null;
  const dni = confirmData?.dni || confirmData?.turnDetail?.dni || null;
  const bookedAtUnix =
    confirmData?.bookedAtUnix || confirmData?.turnDetail?.bookedAtUnix || null;
  const voucherIncomplete = Boolean(confirmData?.voucherIncomplete);
  const usesShare = canUseWebShare();

  const generateImage = async () => {
    if (!turnDetailRef) return null;

    setIsGenerating(true);
    message.loading("Generando comprobante...", 0);

    try {
      // Esperar a que las imágenes se carguen
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvas = await html2canvas(turnDetailRef, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
        onclone: (document) => {
          // Asegurarse de que las imágenes estén cargadas
          const images = document.getElementsByTagName("img");
          return new Promise((resolve) => {
            const loadImages = Array.from(images).map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });
            });
            Promise.all(loadImages).then(resolve);
          });
        },
      });

      message.destroy();
      setIsGenerating(false);
      return canvas;
    } catch (error) {
      console.error("Error al generar la imagen:", error);
      message.destroy();
      message.error("Error al generar el comprobante");
      setIsGenerating(false);
      return null;
    }
  };

  const handleVoucherAction = async () => {
    const canvas = await generateImage();
    if (!canvas) return;

    const filename = `comprobante-turno-${new Date().getTime()}.png`;

    try {
      if (usesShare) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );
        const file = new File([blob], filename, { type: "image/png" });

        if (!navigator.canShare || navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Comprobante de Turno",
          });
          return;
        }
      }

      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error al obtener el comprobante:", error);
      message.error(
        usesShare
          ? "Error al compartir el comprobante"
          : "Error al descargar el comprobante",
      );
    }
  };

  // Renderiza el componente solo si turnDetail existe
  if (!confirmData || !turnDetail) return null;

  return (
    <div className="confirm-turn">
      <div className="confirm-turn__intro">
        <h1 className="confirm-turn__title">¡Turno confirmado!</h1>
        <p className="confirm-turn__text">
          Su confirmación de turno ha sido enviada a su correo electrónico.
        </p>
        <p className="confirm-turn__text">
          Será redirigido a la página de inicio en{" "}
          <strong>{secondsLeft}</strong> segundos...
        </p>
        {voucherIncomplete ? (
          <p className="confirm-turn__text">
            El turno está confirmado, pero no se pudo generar el código para
            cancelar. Contactá al centro si necesitás anularlo.
          </p>
        ) : null}
      </div>

      <div className="confirm-turn__actions">
        <Button
          type="primary"
          icon={usesShare ? <ShareAltOutlined /> : <DownloadOutlined />}
          onClick={handleVoucherAction}
          disabled={isGenerating}
          className="confirm-turn__btn"
        >
          {usesShare ? "Compartir comprobante" : "Descargar comprobante"}
        </Button>
        <Button
          type="primary"
          icon={<HomeOutlined />}
          onClick={goHome}
          className="confirm-turn__btn"
        >
          Ir al inicio
        </Button>
      </div>

      <div className="confirm-turn__card" ref={setTurnDetailRef}>
        <TurnDetail
          turnDetail={turnDetail}
          doctor={doctor}
          turnType={turnType}
          description={description}
          cancelLink={cancelLink}
          direction={direction}
          phone={phone}
          dni={dni}
          bookedAtUnix={bookedAtUnix}
        />
      </div>
    </div>
  );
}
